import { useEffect, useState } from "react";
import { Download, Share, Plus, X, Sparkles, Wifi, Zap, Smartphone, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import appIcon from "@/assets/sisthoko-icon.png";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

const DISMISS_KEY = "install-cta-dismissed-v2";
// Clean up any persisted dismissal from earlier versions — the install bar
// now reappears on every refresh by design.
if (typeof window !== "undefined") {
  try { localStorage.removeItem(DISMISS_KEY); } catch {}
}

function useIsTouch() {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 1024px)");
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return matches;
}

// Shared install state hook — used by both the sticky bar and the in-page section
function useInstallState() {
  const isTouch = useIsTouch();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    const ua = window.navigator.userAgent.toLowerCase();
    setIsIOS(/iphone|ipad|ipod/.test(ua));
    setIsStandalone(
      window.matchMedia("(display-mode: standalone)").matches ||
        (window.navigator as any).standalone === true,
    );

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const triggerNativeInstall = async () => {
    if (!deferredPrompt) return false;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    return outcome === "accepted";
  };

  return { isTouch, deferredPrompt, isIOS, isStandalone, triggerNativeInstall };
}

// ── Install Sheet (shared modal with instructions) ──────────────────────────
function InstallSheet({
  open,
  onOpenChange,
  isIOS,
  deferredPrompt,
  onInstall,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  isIOS: boolean;
  deferredPrompt: BeforeInstallPromptEvent | null;
  onInstall: () => void;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-3xl px-0 pb-8">
        <SheetHeader className="px-6 text-center">
          <div className="mx-auto mb-3">
            <img
              src={appIcon}
              alt="Sis Thoko"
              className="h-20 w-20 rounded-2xl shadow-elevated mx-auto"
            />
          </div>
          <SheetTitle className="text-2xl text-center">Add Sis Thoko to your home screen</SheetTitle>
          <p className="text-sm text-muted-foreground text-center mt-1">
            Get one-tap access. No app store required.
          </p>
        </SheetHeader>

        <div className="px-6 mt-6 space-y-3">
          {[
            { icon: Zap, title: "Faster", desc: "Opens instantly from your home screen" },
            { icon: Wifi, title: "Works offline", desc: "Browse your basket without signal" },
            { icon: Sparkles, title: "Always up to date", desc: "No app store updates needed" },
          ].map((f) => (
            <div key={f.title} className="flex items-start gap-3 p-3 rounded-xl bg-muted/40">
              <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <f.icon className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">{f.title}</p>
                <p className="text-xs text-muted-foreground">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="px-6 mt-6">
          {isIOS ? (
            <div className="rounded-2xl border border-border p-4 bg-card">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                How to install on iPhone
              </p>
              <ol className="space-y-3">
                <li className="flex items-center gap-3">
                  <span className="h-6 w-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center shrink-0">1</span>
                  <span className="text-sm text-foreground flex items-center gap-1.5">
                    Tap the <Share className="h-4 w-4 inline text-primary" /> Share button below
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="h-6 w-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center shrink-0">2</span>
                  <span className="text-sm text-foreground flex items-center gap-1.5">
                    Choose <Plus className="h-4 w-4 inline text-primary" /> "Add to Home Screen"
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="h-6 w-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center shrink-0">3</span>
                  <span className="text-sm text-foreground">Tap "Add" — and you're done!</span>
                </li>
              </ol>
            </div>
          ) : (
            <Button
              onClick={onInstall}
              disabled={!deferredPrompt}
              className="w-full gradient-primary text-primary-foreground h-12 text-base font-semibold shadow-md"
            >
              <Download className="mr-2 h-5 w-5" />
              {deferredPrompt ? "Install Sis Thoko" : "Open browser menu → Install app"}
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ── Sticky bottom bar (dismissible) ─────────────────────────────────────────
export function InstallAppCTA() {
  const { isTouch, deferredPrompt, isIOS, isStandalone, triggerNativeInstall } = useInstallState();
  // Dismissal is intentionally NOT persisted — the bar reappears on every
  // page refresh so users who closed it by mistake can recover it easily.
  const [dismissed, setDismissed] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  if (!isTouch || dismissed || isStandalone) return null;
  // On Android (non-iOS) only show once the browser has handed us a real
  // install prompt — so clicking "Install" always triggers the OS dialog,
  // never an instructions screen.
  if (!isIOS && !deferredPrompt) return null;

  const handleInstall = async () => {
    if (deferredPrompt) {
      const accepted = await triggerNativeInstall();
      if (accepted) setDismissed(true);
      setSheetOpen(false);
    } else {
      // iOS — no programmatic install API exists; show Add-to-Home-Screen steps.
      setSheetOpen(true);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
  };

  return (
    <>
      <div className="fixed bottom-0 inset-x-0 z-[60] px-3 pb-3 pt-2 pointer-events-none">
        <div
          className="pointer-events-auto mx-auto max-w-md rounded-2xl bg-white border border-border shadow-elevated overflow-hidden animate-in slide-in-from-bottom-4 duration-500"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <div className="flex items-center gap-3 p-3">
            <div className="relative shrink-0">
              <img src={appIcon} alt="Sis Thoko app icon" className="h-14 w-14 rounded-xl shadow-md" />
              <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-success text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white">
                <Sparkles className="h-3 w-3" />
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[15px] font-bold text-foreground leading-tight">Install Sis Thoko</p>
              <p className="text-[12px] text-muted-foreground leading-snug truncate">
                Faster access · Works offline · Free
              </p>
            </div>
            <Button
              size="sm"
              onClick={handleInstall}
              className="gradient-primary text-primary-foreground font-semibold shadow-md shrink-0 h-10 px-4"
            >
              Install
            </Button>
            <button
              onClick={handleDismiss}
              aria-label="Dismiss"
              className="h-8 w-8 -mr-1 rounded-full hover:bg-muted text-muted-foreground flex items-center justify-center shrink-0"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <InstallSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        isIOS={isIOS}
        deferredPrompt={deferredPrompt}
        onInstall={handleInstall}
      />
    </>
  );
}

// ── In-page section (always visible, never dismissed) ───────────────────────
// Renders on the landing page so users who close the sticky bar still have a
// clear, persistent way to install the app.
export function InstallAppSection() {
  const { isTouch, deferredPrompt, isIOS, isStandalone, triggerNativeInstall } = useInstallState();
  const [sheetOpen, setSheetOpen] = useState(false);

  if (!isTouch || isStandalone) return null;
  // On Android, only render once the native install prompt is available so
  // tapping the button performs a real install instead of showing steps.
  if (!isIOS && !deferredPrompt) return null;

  const handleInstall = async () => {
    if (deferredPrompt) {
      await triggerNativeInstall();
      setSheetOpen(false);
    } else {
      // iOS only.
      setSheetOpen(true);
    }
  };

  return (
    <section id="install-app" className="px-4 py-12 md:py-16">
      <div className="container mx-auto max-w-4xl">
        <div className="relative rounded-3xl gradient-sapphire p-6 md:p-10 shadow-elevated overflow-hidden">
          <div className="absolute inset-0 bg-mesh opacity-30 pointer-events-none" />

          <div className="relative flex flex-col items-center text-center md:flex-row md:text-left md:items-center gap-6">
            <img
              src={appIcon}
              alt="Sis Thoko app icon"
              className="h-24 w-24 rounded-2xl shadow-elevated shrink-0"
            />

            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wider text-white/70 mb-2 inline-flex items-center gap-1.5">
                <Smartphone className="h-3.5 w-3.5" /> Get the app
              </p>
              <h3 className="text-2xl md:text-3xl font-bold text-white text-balance leading-tight">
                Install Sis Thoko on your phone
              </h3>
              <p className="text-sm md:text-base text-white/85 mt-2 leading-relaxed">
                One-tap access from your home screen — faster, offline-friendly, no app store needed.
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-3 justify-center md:justify-start">
                <Button
                  size="lg"
                  onClick={handleInstall}
                  variant="secondary"
                  className="shadow-lg font-semibold"
                >
                  <Download className="mr-2 h-4 w-4" />
                  {isIOS ? "Show install steps" : "Install app"}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>

                <div className="flex items-center gap-3 text-[11px] text-white/70">
                  <span className="inline-flex items-center gap-1"><Zap className="h-3 w-3" /> Faster</span>
                  <span className="inline-flex items-center gap-1"><Wifi className="h-3 w-3" /> Offline</span>
                  <span className="inline-flex items-center gap-1"><Sparkles className="h-3 w-3" /> Free</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <InstallSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        isIOS={isIOS}
        deferredPrompt={deferredPrompt}
        onInstall={handleInstall}
      />
    </section>
  );
}
